import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ErrorStateMatcher, MAT_DATE_FORMATS } from '@angular/material/core';
import { FromToLabel } from '../../search-page/search-param.model';
import { SE_UI_DATE_FORMATS } from '../se-ui-date-formats';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';

@Component({
    selector: 'se-from-to',
    templateUrl: './se-from-to.component.html',
    styleUrls: ['./se-from-to.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: SE_UI_DATE_FORMATS },
    ],
})
export class SeFromToComponent implements OnInit, OnDestroy {

    // Costanti
    today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59);
    minFromDate = new Date(1900, 1, 1, 0, 0, 0);
    maxFromDate = new Date(2050, 12, 31, 23, 59, 59);
    minToDate = new Date(1900, 1, 1, 0, 0, 0);
    maxToDate = new Date(2050, 12, 31, 23, 59, 59);

    // Matcher per gli errori
    fromPickerMatcher: FromToPickerStateMatcher = new FromToPickerStateMatcher();
    toPickerMatcher: FromToPickerStateMatcher = new FromToPickerStateMatcher();

    private isOnErrorFromSubscriber: Subscription;
    private isOnErrorToSubscriber: Subscription;

    @Input()
    name: string;

    @Input()
    id: string;

    @Input()
    disabled: boolean = false;

    @Input()
    autofocus: boolean = false;

    @Input()
    placeholder: FromToLabel = {
            from: 'From',
            to: 'To',
            label: 'test'
    };

    @Input()
    fromToModel = { from: undefined, to: undefined };

    @Input()
    predefiniteIntervals: SeFromToInterval[] = [
        new SeFromToInterval(1, 0, 0, 0),
        new SeFromToInterval(2, 0, 0, 0),
        new SeFromToInterval(0, 1, 0, 0),
        new SeFromToInterval(0, 2, 0, 0),
        new SeFromToInterval(0, 0, 1, 0),
        new SeFromToInterval(0, 0, 6, 0),
        new SeFromToInterval(0, 0, 0, 1)
    ];

    @Output()
    fromToDatesChange: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    searchRequired: EventEmitter<any> = new EventEmitter<any>();

    // Two Way
    @Output() isOnErrorChange = new EventEmitter();
    _isOnError: boolean = false;

    @Input()
    get isOnError() {
        return this._isOnError;
    }
    set isOnError(value) {
        if (this._isOnError != value) {
            this._isOnError = value;
        }
    }

    // Constructor

    constructor(private translateService: TranslateService) {
    }

    // private functions
    private setupUtcBeginEnd(): void {

        // imposto la durata fino alla fine della giornata
        if (this.fromToModel && this.fromToModel.to) {
            if (!!this.fromToModel.to.setHours) // Date
                this.fromToModel.to.setHours(23, 59, 59);
            else if (!!this.fromToModel.to.set) { // Moment
                this.fromToModel.to = this.fromToModel.to.utc(true); // Aggiunge il fuso, poi lo toglierà automaticamente il toJson
                this.fromToModel.to.set({ hour: 23, minute: 59, second: 59, millisecond: 0 });
            }

            this.toPickerMatcher.to = this.fromToModel.to;
            this.fromPickerMatcher.to = this.fromToModel.to;
        }

        if (this.fromToModel && this.fromToModel.from) {
            if (!!this.fromToModel.from.setHours) // Date
               this.fromToModel.from.setHours(0, 0, 0);
            else if (!!this.fromToModel.from.set) { // Moment
                this.fromToModel.from = this.fromToModel.from.utc(true);
                this.fromToModel.from.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            }

            this.toPickerMatcher.from = this.fromToModel.from;
            this.fromPickerMatcher.from = this.fromToModel.from;
        }
    }

    // public functions

    ngOnDestroy() {
        if (this.isOnErrorFromSubscriber)
            this.isOnErrorFromSubscriber.unsubscribe();

        if (this.isOnErrorToSubscriber)
            this.isOnErrorToSubscriber.unsubscribe();
    }

    ngOnInit(): void {
        this.setupUtcBeginEnd();

        this.isOnErrorFromSubscriber = this.fromPickerMatcher.isOnError.subscribe((value) => {
            this.isOnErrorHandler(value);
        });

        this.isOnErrorToSubscriber = this.toPickerMatcher.isOnError.subscribe((value) => {
            this.isOnErrorHandler(value);
        });
    }

    private isOnErrorHandler(value: boolean) {
        if (this.isOnError != value) {
            this.isOnError = value;
            setTimeout(() => {// Il timeout serve per la sincronizzazione altrimenti abbiamo l'errore i debug
                this.isOnErrorChange.emit(value);
            }, 0);
        }
    }

    getLabelForInterval(interval: SeFromToInterval):string {
        let label: string = '';

        if (interval.days)
            label = interval.days.toString() + ' ' + this.translateService.instant('Days');

        if (interval.weeks)
            label = interval.weeks.toString() + ' ' + this.translateService.instant('Weeks');

        if (interval.months)
            label = interval.months.toString() + ' ' + this.translateService.instant('Months');

        if (interval.years)
            label = interval.years.toString() + ' ' + this.translateService.instant('Year');

        return label;
    }

    onFromToChange(_) {
        this.setupUtcBeginEnd();

        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    resetDateFrom(e: Event) {
        e.preventDefault(); // Altrimenti parte la ricerca sulla search page

        this.fromToModel.from = undefined;

        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    resetDateTo(e) {
        e.preventDefault(); // Altrimenti parte la ricerca sulla search page

        this.fromToModel.to = undefined;

        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    onSpaceBar(_: KeyboardEvent) {
        // Imposto dalle 00.00 alle 23.59 di oggi
        let from: moment.Moment = moment(Date.now());

        from.hour(0);
        from.minute(0);
        from.second(0);

        this.fromToModel.from = from.utc(true);

        let to: moment.Moment = moment(Date.now());
        to.hour(23);
        to.minute(59);
        to.second(59);

        this.fromToModel.to = to.utc(true);

        this.fromToDatesChange.emit({ propertyName: this.id });
    }

    openPredefiniteInterval(e) {
        e.preventDefault(); // Altrimenti parte la ricerca sulla search page
    }

    setPredefiniteInterval(interval: SeFromToInterval) {
        this.fromToModel.from = new Date(new Date().getFullYear() - interval.getYears(), new Date().getMonth() - interval.getMonths(), new Date().getDate() - interval.getDays());
        this.fromToModel.to = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 23, 59, 59);
    }

    onSearchRequired(e) {
        this.setupUtcBeginEnd();

        e.preventDefault();
        this.searchRequired.emit();
    }

    getDateFormat() {
        return SE_UI_DATE_FORMATS.display.dateInput;
    }
}

export class SeFromToInterval {
    constructor(public days: number, public weeks: number, public months: number, public years: number) {
    }

    getDays(): number {
        if (!this.weeks)
            return this.days;
        else
            return this.weeks * 7 + this.days;
    }

    getMonths(): number {
        return this.months;
    }

    getYears(): number {
        return this.years;
    }
}

export class FromToPickerStateMatcher implements ErrorStateMatcher {
    isOnError: BehaviorSubject<boolean> = new BehaviorSubject(false);
    to;
    from;
    fromAfterTo: boolean;
    toBeforeFrom: boolean;

    isErrorState(control: FormControl | null, _: FormGroupDirective | NgForm | null): boolean {
        let _isOnError = !!(control && control.invalid);
        this.fromAfterTo = this.from > this.to;
        _isOnError = _isOnError || this.fromAfterTo == true;
        if (_isOnError != this.isOnError.value)
            this.isOnError.next(_isOnError);

        return _isOnError
    }
}

